'use client'

// TransitionBridge — zoom-into-letter portal.
//
// LAYER STACK (bottom → top)
//   0  background div      — color-scrubbed #120B03 → #F3EAC8  (NEVER TOUCH)
//   2  video               — full-viewport, revealed through growing clip-path circle
//   10 quote container     — full-viewport flex div, scales 1→200 around "o" center
//   20 chrome              — editorial labels, above everything
//
// SCROLL PHASES  (scrub:0.6, pin +=210%)
//   A  0.00–0.10   background darkens, text already visible
//   B  0.10–0.55   scale 1→200; text opacity driven by onUpdate (directional):
//                  video clip-path 2%→120%, opacity 0→1
//   C  0.50–0.95   video currentTime scrubbed; chapter foreshadow at 0.75
//   D  (removed)   video stays visible; pin releases and Awards scrolls in normally

import { useRef, useCallback, useEffect } from 'react'
import { useGSAP } from '@gsap/react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

const CREAM      = '#F3EAC8'
const CREAM_MID  = 'rgba(244,236,211,0.80)'
const CREAM_LOW  = 'rgba(244,236,211,0.48)'
const LIME       = '#9FAF08'

const FONT_BODY  = 'var(--font-body)'
const FONT_DISP  = 'var(--font-display)'

export default function TransitionBridge() {
  const t = useTranslations('bridge')
  const sectionRef      = useRef<HTMLElement>(null)
  const viewportRef     = useRef<HTMLDivElement>(null)
  const backgroundRef   = useRef<HTMLDivElement>(null)
  const creamRef        = useRef<HTMLDivElement>(null)
  const videoRef        = useRef<HTMLVideoElement>(null)
  const quoteWrapRef    = useRef<HTMLDivElement>(null)
  const preTextRef      = useRef<HTMLSpanElement>(null)   // "Ground remembers. R"
  const portalLetterRef = useRef<HTMLSpanElement>(null)   // "o"  ← focal point
  const postTextRef     = useRef<HTMLSpanElement>(null)   // "ots return. Life..."
  const chromeRef       = useRef<HTMLDivElement>(null)
  const chapterRef      = useRef<HTMLDivElement>(null)

  // Portal center in viewport %
  const pxRef = useRef(50)
  const pyRef = useRef(50)

  // Video scrub
  const rafRef        = useRef(0)
  const targetTimeRef = useRef(0)

  const reduced = useReducedMotion()

  // ── Focal-point measurement ─────────────────────────────────────────────────
  // Runs after fonts load + on every resize / ScrollTrigger refresh.
  const calcPortal = useCallback(() => {
    const letter = portalLetterRef.current
    const vp     = viewportRef.current
    if (!letter || !vp) return
    const lr = letter.getBoundingClientRect()
    const vr = vp.getBoundingClientRect()
    pxRef.current = ((lr.left + lr.width  / 2 - vr.left) / vr.width)  * 100
    pyRef.current = ((lr.top  + lr.height / 2 - vr.top)  / vr.height) * 100
  }, [])

  useEffect(() => {
    document.fonts.ready.then(calcPortal)
    let t: ReturnType<typeof setTimeout>
    const onResize = () => {
      clearTimeout(t)
      t = setTimeout(() => { calcPortal(); ScrollTrigger.refresh() }, 150)
    }
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t) }
  }, [calcPortal])

  // ── Lazy preload: kick the browser into fetching the timelapse only when
  // the section is approaching the viewport. Saves bandwidth on first paint
  // (mobile especially) but ensures the video has buffered enough for the
  // scroll-scrub by the time the user reaches it. iOS/Android browsers
  // enforce preload="metadata" strictly and never fetch chunks needed for
  // currentTime writes; we have to flip to "auto" + .load() ourselves.
  useEffect(() => {
    const v = videoRef.current
    const s = sectionRef.current
    if (!v || !s) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          v.preload = 'auto'
          v.load()
          io.disconnect()
        }
      },
      { rootMargin: '200% 0px 200% 0px' }, // start preloading well before viewport
    )
    io.observe(s)
    return () => io.disconnect()
  }, [])

  // ── RAF video-scrub loop ─────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      const v = videoRef.current
      if (v && v.readyState >= 2) {
        v.currentTime = targetTimeRef.current
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  // ── GSAP ────────────────────────────────────────────────────────────────────
  useGSAP(
    () => {
      gsapSetup()
      const vp = viewportRef.current
      if (!vp) return

      // Reduced-motion: flat, no zoom
      if (reduced) {
        gsap.set(creamRef.current,     { opacity: 1 })
        gsap.set(quoteWrapRef.current, { autoAlpha: 1, scale: 1 })
        gsap.set(chapterRef.current,   { autoAlpha: 1, y: 0 })
        gsap.set(videoRef.current,     { autoAlpha: 0 })
        return
      }

      const isMobile = window.matchMedia(
        '(max-width: 767px), (hover: none) and (pointer: coarse)',
      ).matches

      // ── Initial states
      gsap.set(creamRef.current,  { opacity: 0 })
      gsap.set(videoRef.current,  { opacity: 0 })
      // Quote and chrome visible from the first frame — no empty brown canvas
      gsap.set(quoteWrapRef.current,  { autoAlpha: 1, scale: 1 })
      gsap.set(chromeRef.current,     { autoAlpha: 1, y: 0 })
      gsap.set(chapterRef.current,    { autoAlpha: 0, y: 24 })

      // Proxy drives clip-path + video opacity in onUpdate
      const proxy = { circle: 2, vop: 0 }   // start at 2% — matches "o" counter size

      const pushClip = () => {
        const v = videoRef.current
        if (!v) return
        v.style.clipPath = `circle(${proxy.circle.toFixed(2)}% at ${pxRef.current.toFixed(2)}% ${pyRef.current.toFixed(2)}%)`
        v.style.opacity  = proxy.vop.toFixed(4)
      }
      // Apply initial clip-path with correct portal position once fonts are ready
      document.fonts.ready.then(() => {
        calcPortal()
        pushClip()
      })

      // ── Master timeline
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: vp,
          start:   'top top',
          end:     () => `+=${isMobile ? 150 : 210}%`,
          pin:     true,
          pinSpacing: true,
          scrub:   0.2,
          anticipatePin: 1,
          invalidateOnRefresh: true,

          onRefresh: () => {
            // Re-measure "o" position after every reflow; update transform-origin live
            calcPortal()
            const qw = quoteWrapRef.current
            if (qw) {
              gsap.set(qw, {
                transformOrigin: `${pxRef.current.toFixed(2)}% ${pyRef.current.toFixed(2)}%`,
              })
            }
            pushClip()
          },

          onUpdate: (self) => {
            const p   = self.progress
            const dir = self.direction   // 1 = forward, -1 = reverse

            // Phase C: map scroll progress → video.currentTime
            // Range 0.50→0.95 gives ~94vh of scroll for the scrub (was ~56vh).
            if (p >= 0.50 && p <= 0.95) {
              const v = videoRef.current
              if (v?.duration) {
                targetTimeRef.current = ((p - 0.50) / 0.45) * v.duration
              }
            }

            // Directional text fade — decoupled from the timeline tween.
            // Forward : full opacity through 0.38, fades out by 0.43 (scale ~55×→100×).
            // Reverse : invisible above 0.25, fades back in by 0.20 (scale ~8×→3×,
            //           both text lines comfortably fit the viewport at scale ≤3×).
            const qw = quoteWrapRef.current
            if (qw) {
              let alpha: number
              if (dir >= 0) {
                alpha = p < 0.38 ? 1 : p < 0.43 ? (0.43 - p) / 0.05 : 0
              } else {
                alpha = p > 0.25 ? 0 : p > 0.20 ? (0.25 - p) / 0.05 : 1
              }
              alpha = Math.max(0, Math.min(1, alpha))
              qw.style.opacity    = String(alpha)
              qw.style.visibility = alpha > 0 ? 'visible' : 'hidden'
            }
          },
        },
      })

      // ── PHASE B — zoom into "o"  0.10 → 0.55  ──────────────────────────────

      // Scale the full-viewport container 1 → 200 around the "o" center
      // (transform-origin is set in onRefresh so it always points at the "o")
      tl.to(quoteWrapRef.current, {
        scale:    200,
        duration: 0.45,
        ease:     'power3.in',
      }, 0.10)

      // Text fade is handled directionally in onUpdate (not here) so forward and
      // reverse can use different thresholds without a single tween compromising both.

      // Video portal circle: starts at 2% (matches "o" counter at scale 1),
      // expands to 120% in sync with the scale, same ease
      tl.to(proxy, {
        circle:   120,
        vop:      1,
        duration: 0.45,
        ease:     'power3.in',
        onUpdate: pushClip,
      }, 0.10)

      // Chrome dims as zoom takes over
      tl.to(chromeRef.current, {
        autoAlpha: 0.4,
        duration:  0.20,
      }, 0.20)

      // ── PHASE C — video scrub  0.55 → 0.90  (currentTime via RAF+onUpdate)

      // Cream overlay fades in → covers the dark gradient under the video
      tl.to(creamRef.current, {
        opacity:  1,
        duration: 0.35,
        ease:     'power2.inOut',
      }, 0.55)

      // Chapter foreshadow emerges
      tl.to(chapterRef.current, {
        autoAlpha: 1,
        y:         0,
        duration:  0.09,
        ease:      'power3.out',
      }, 0.75)

      // Phase D removed — video stays visible at full opacity when scrub ends.
      // Pin releases naturally and normal vertical scroll continues to Awards.

      ScrollTrigger.refresh()
    },
    { scope: sectionRef, dependencies: [reduced], revertOnUpdate: true },
  )

  return (
    <section
      ref={sectionRef}
      id="transition-bridge"
      aria-hidden="true"
      style={{ position: 'relative', zIndex: 1, marginTop: '-16px', background: 'linear-gradient(to right, #060301 0%, #0B0603 30%, #140C04 60%, #221604 100%)' }}
    >
      <div
        ref={viewportRef}
        style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}
      >

        {/* ── LAYER 0: Matches Solution's sol-below horizontal gradient exactly ── */}
        <div
          ref={backgroundRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: 'linear-gradient(to right, #060301 0%, #0B0603 30%, #140C04 60%, #221604 100%)',
          }}
        />

        {/* ── LAYER 1: Cream overlay — fades in during Phase C ─────────────── */}
        <div
          ref={creamRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            backgroundColor: CREAM,
            opacity: 0,
          }}
        />

        {/* ── LAYER 2: Video ───────────────────────────────────────────────── */}
        {/* Clip-path circle grows from "o" center. Because video is BELOW the */}
        {/* text container (z:2 < z:10), the transparent counter of the "o"    */}
        {/* shows the video through the letter's hole as the scale builds.     */}
        <video
          ref={videoRef}
          src="/videos/soil-timelapse.mp4"
          muted
          playsInline
          // Was "auto" — that downloads all 5.5 MB on first paint and starves
          // mobile bandwidth. "metadata" fetches just the header; chunks
          // stream in when we scrub.
          preload="metadata"
          crossOrigin="anonymous"
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 2,
            opacity: 0,
            clipPath: 'circle(2% at 50% 50%)',  // updated by pushClip() after font-ready
            willChange: 'clip-path, opacity',
          }}
        />

        {/* ── LAYER 10: Pull-quote — full-viewport, scales 1→200 ──────────── */}
        <div
          ref={quoteWrapRef}
          data-tb-quote
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(48px, 8vw, 120px)',
            pointerEvents: 'none',
            willChange: 'transform, opacity',
            // Crisper rasterisation at any scale: dedicated GPU layer + AA hints.
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          <p
            style={{
              fontFamily: FONT_DISP,
              fontWeight: 400,
              fontSize: 'clamp(22px, 2.6vw, 46px)',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: CREAM,
              maxWidth: '34ch',
              margin: 0,
              textAlign: 'center',
              textRendering: 'geometricPrecision',
            }}
          >
            <span ref={preTextRef}>{t('pullQuotePre')}</span>

            {/* Portal focal point — same font/style as the rest, LIME colour only */}
            <span
              ref={portalLetterRef}
              style={{
                fontFamily: FONT_DISP,
                color:      LIME,
                display:    'inline-block',
              }}
            >
              o
            </span>

            <span ref={postTextRef}>
              {t('pullQuotePost')}
            </span>
          </p>
        </div>

        {/* ── LAYER 20: Editorial chrome ───────────────────────────────────── */}
        <div
          ref={chromeRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          <div
            data-tb-chrome="left"
            style={{
              position: 'absolute',
              top: 40,
              left: 48,
              fontFamily: FONT_BODY,
              fontSize: 11,
              letterSpacing: '0.38em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: CREAM_MID,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: LIME,
                boxShadow: `0 0 10px ${LIME}`,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span>{t('chromeLeft')}</span>
          </div>

          <div
            data-tb-chrome="right"
            style={{
              position: 'absolute',
              top: 40,
              right: 48,
              fontFamily: FONT_BODY,
              fontSize: 11,
              letterSpacing: '0.38em',
              textTransform: 'uppercase',
              fontWeight: 500,
              color: CREAM_LOW,
            }}
          >
            {t('chromeRight')}
          </div>
        </div>

        {/* ── Chapter foreshadow — bottom-center, reveals at Phase C 0.75 ──── */}
        {/* Wrapped in a dark glass chip so the label stays legible against
            the bright grass timelapse at the end of the video scrub. */}
        <div
          ref={chapterRef}
          data-tb-chip-wrap
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 52,
            left: 0,
            right: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            data-tb-chip
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 16,
              padding: '8px 20px',
              borderRadius: 999,
              background: 'rgba(20, 14, 4, 0.55)',
              backdropFilter: 'blur(10px) saturate(160%)',
              WebkitBackdropFilter: 'blur(10px) saturate(160%)',
              border: '1px solid rgba(243, 234, 200, 0.14)',
              boxShadow: '0 4px 18px rgba(0,0,0,0.28)',
              fontFamily: FONT_BODY,
              fontSize: 12,
              letterSpacing: '0.44em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: CREAM,
              textShadow: '0 1px 2px rgba(0,0,0,0.45)',
            }}
          >
            <span style={{ width: 36, height: 1, background: 'rgba(243, 234, 200, 0.45)', display: 'inline-block' }} />
            <span>{t('chapterLabel')}</span>
            <span style={{ width: 36, height: 1, background: 'rgba(243, 234, 200, 0.45)', display: 'inline-block' }} />
          </div>
        </div>

        {/* Grain overlay */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/grain.svg)',
            backgroundSize: '200px 200px',
            opacity: 0.08,
            mixBlendMode: 'screen',
            zIndex: 30,
            pointerEvents: 'none',
          }}
        />
      </div>

      <style>{`
        @media (max-width: 768px) {
          /* Stack the corner chrome labels — at narrow widths their
             absolute left/right positions overlap and produce mangled,
             unreadable text. Top-left stays; right label tucked below. */
          #transition-bridge [data-tb-chrome="left"] {
            top: 18px !important;
            left: 18px !important;
            right: auto !important;
            font-size: 9px !important;
            letter-spacing: 0.26em !important;
            gap: 8px !important;
            max-width: calc(100vw - 36px);
          }
          #transition-bridge [data-tb-chrome="right"] {
            display: none !important;
          }
          /* Pull quote: extra rasterisation hints on mobile so text
             stays as crisp as possible during the 200× scrub-zoom. */
          #transition-bridge [data-tb-quote] p {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: geometricPrecision;
          }

          /* Bottom chapter chip — single line, never wraps */
          #transition-bridge [data-tb-chip-wrap] {
            bottom: 28px !important;
            padding: 0 14px !important;
          }
          #transition-bridge [data-tb-chip] {
            font-size: 9px !important;
            letter-spacing: 0.22em !important;
            padding: 6px 14px !important;
            gap: 10px !important;
            max-width: calc(100vw - 28px);
            white-space: nowrap !important;
            flex-wrap: nowrap !important;
          }
          #transition-bridge [data-tb-chip] > span {
            white-space: nowrap !important;
            flex-shrink: 0 !important;
          }
          #transition-bridge [data-tb-chip] > span:first-child,
          #transition-bridge [data-tb-chip] > span:last-child {
            width: 14px !important;
          }
        }
      `}</style>
    </section>
  )
}
